/**
 * The things that appear for a fraction of a second and then do not.
 *
 * None of it affects anything — no damage, no collision, no state — which is
 * exactly why it belongs somewhere other than the run: it is the only part of
 * a fight that can be changed without being able to break one.
 *
 * Everything here is made of edges. No soft sprites, no expanding translucent
 * spheres, no clouds: a world of flat white planes and faceted crystal has no
 * vocabulary for a smoke puff, and one dropped into it reads as an effect
 * borrowed from a different game. A hit throws chips. A death fractures. A
 * shot is a hard white line and a hard white core. What makes them legible is
 * that they are the only things on screen bright enough to bloom, and what
 * makes them cheap is that they are all the same twenty-sided solid.
 */

import * as THREE from 'three';

/** How long each kind lives, in seconds. */
const TRACER_LIFE = 0.055;
/** A muzzle core is 40 ms: long enough to see, short enough not to be a lamp. */
const CORE_LIFE = 0.04;
const SHARD_LIFE = 0.85;
/**
 * How many arrivals can be lit at once.
 *
 * A point light is not free and the tiers that need this most are the ones
 * that can least afford it, so there is a fixed handful and an arrival that
 * cannot get one simply fades in unlit.
 */
/** How many loose pieces a floor may have lying on it at once. */
const MAX_PARTS = 90;
const SPAWN_LIGHTS = 4;

/**
 * One shape for every piece of debris in the game.
 *
 * The same solid the hostiles are built from, which is not a coincidence: a
 * chip off one of them should look like a chip off one of them. Shared, so a
 * hundred shards in the air are a hundred draw calls of one geometry rather
 * than a hundred allocations.
 */
const SHARD = new THREE.IcosahedronGeometry(0.5, 0);

/** How many shards can be in the air before the oldest are reused. */
const SHARDS: Record<string, number> = { mobile: 40, balanced: 90, high: 160 };

interface Shard {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  spin: THREE.Vector3;
  life: number;
  size: number;
}

export class Effects {
  /** Everything drawn here, for the caller to hang off its own root. */
  readonly group = new THREE.Group();
  private readonly tracers = new THREE.Group();
  private readonly debris = new THREE.Group();
  readonly muzzle: THREE.PointLight;

  /** Free spawn lights, handed out one per arriving enemy. */
  private readonly spare: THREE.PointLight[] = [];

  private readonly shards: Shard[] = [];
  private nextShard = 0;
  /** The muzzle core and its halo: one of each, reused every shot. */
  private readonly core: THREE.Mesh;
  private readonly halo: THREE.Mesh;
  private coreLife = 0;
  /** The flash a fracture opens with, and how long it has left. */
  private readonly burstCore: THREE.Mesh;
  private burstLife = 0;
  /** The map's accent, for the one part of a muzzle flash that is not white. */
  private accent = new THREE.Color(0x1d4ed8);

  /*
   * The marks shots leave on the world.
   *
   * A round that hits a wall used to leave nothing at all, so the only
   * evidence of a miss was that nothing happened — which makes a weapon feel
   * broken rather than makes you feel inaccurate. These are the cheapest
   * possible version: one shared circle geometry, one shared material, a fixed
   * ring of instances handed out in turn, each turned to face the surface it
   * landed on and lifted a millimetre off it so it does not fight the wall for
   * the depth buffer.
   *
   * No texture and no normal map. A hole in a pale wall is a dark disc with a
   * softer rim, and that is exactly what a radial gradient on a small quad
   * gives — at a fraction of the cost of the thing it is imitating, and with
   * nothing to download.
   */
  private readonly holes: THREE.Mesh[] = [];
  private nextHole = 0;
  private holeTex: THREE.Texture | null = null;

  /*
   * The pieces of something that has stopped being a thing.
   *
   * A kill used to be a fade: the body dimmed where it stood and was gone,
   * which reads as the enemy being deleted rather than as it dying. Coming
   * apart is a different event — the pieces are the same shapes that were
   * walking about a frame ago, they fall, they land, and they stay where they
   * landed. You can walk into them afterwards, which is the part that makes
   * the floor feel like somewhere things happened.
   *
   * Cloned rather than borrowed, because the body itself goes back to a pool
   * and will be walking about again shortly.
   */
  private readonly parts: {
    mesh: THREE.Mesh; vel: THREE.Vector3; spin: THREE.Vector3; radius: number; resting: boolean;
    /** Where it came apart, so a piece that leaves the world can be noticed. */
    bornY: number;
  }[] = [];
  private readonly remains = new THREE.Group();

  constructor(tier: keyof typeof SHARDS | string = 'balanced') {
    // Short range and quick to fade: a muzzle flash that lights the far wall
    // reads as a lamp being switched on rather than as a shot.
    this.muzzle = new THREE.PointLight(0xffd9a0, 0, 14, 2);
    this.group.add(this.tracers, this.debris, this.muzzle, this.remains);

    /*
     * One canvas for the hole, drawn once.
     *
     * Dark in the middle, transparent at the rim, with a faint bright ring
     * just inside the edge: that ring is the lip of displaced material and it
     * is what stops the mark reading as a sticker.
     */
    const HOLES = 48;
    const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    let holeMat: THREE.MeshBasicMaterial | null = null;
    if (c) {
      c.width = c.height = 64;
      const g = c.getContext('2d')!;
      const rad = g.createRadialGradient(32, 32, 2, 32, 32, 30);
      rad.addColorStop(0, 'rgba(8,10,13,0.92)');
      rad.addColorStop(0.55, 'rgba(12,15,19,0.62)');
      rad.addColorStop(0.78, 'rgba(190,198,208,0.30)');
      rad.addColorStop(1, 'rgba(0,0,0,0)');
      g.fillStyle = rad;
      g.beginPath();
      g.arc(32, 32, 30, 0, Math.PI * 2);
      g.fill();
      const tex = new THREE.CanvasTexture(c);
      tex.colorSpace = THREE.SRGBColorSpace;
      this.holeTex = tex;
      holeMat = new THREE.MeshBasicMaterial({
        map: tex, transparent: true, depthWrite: false, toneMapped: false,
      });
    }
    if (holeMat) {
      const quad = new THREE.PlaneGeometry(1, 1);
      for (let i = 0; i < HOLES; i++) {
        const m = new THREE.Mesh(quad, holeMat);
        m.visible = false;
        m.renderOrder = 1;
        this.group.add(m);
        this.holes.push(m);
      }
    }

    /*
     * Made once, and never hidden.
     *
     * `visible = false` takes a light out of the scene as far as the renderer
     * is concerned, and the number of lights it can see is part of every
     * material's program key — so hiding one and showing it again recompiles
     * every shader in the game. Enemies arrive constantly, which made that a
     * hitch every few seconds.
     *
     * An idle light is a light at zero intensity instead. It costs a few
     * uniforms per material and nothing else, and the count never moves.
     */
    for (let i = 0; i < SPAWN_LIGHTS; i++) {
      const l = new THREE.PointLight(0xbfe4ff, 0, 11, 2);
      this.group.add(l);
      this.spare.push(l);
    }

    /*
     * The core is white and the halo is the map's colour.
     *
     * A muzzle flash that is entirely one warm orange is a muzzle flash from a
     * different game; what a shot actually looks like from behind the gun is a
     * white overexposed centre with a coloured edge to it. Both are drawn with
     * tone mapping off, which is what puts them over the bloom threshold that
     * nothing else in the world can reach.
     */
    this.core = new THREE.Mesh(
      SHARD,
      new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false, transparent: true, depthWrite: false }),
    );
    this.halo = new THREE.Mesh(
      new THREE.RingGeometry(0.09, 0.2, 12),
      new THREE.MeshBasicMaterial({
        color: this.accent, toneMapped: false, transparent: true,
        depthWrite: false, side: THREE.DoubleSide,
      }),
    );
    /*
     * The core a fracture lets out.
     *
     * Its own mesh rather than a borrowed shard, because a shard is a lit
     * standard material and tone mapping rolls it off below the bloom
     * threshold however high its emissive is set: it would be a bright object
     * and never a light. Unlit, so it is over the threshold by construction —
     * the same trick as the muzzle core, for the same reason.
     */
    this.burstCore = new THREE.Mesh(
      SHARD,
      new THREE.MeshBasicMaterial({ color: 0xff3158, toneMapped: false, transparent: true, depthWrite: false }),
    );
    this.burstCore.visible = false;
    this.core.visible = false;
    this.halo.visible = false;
    this.core.renderOrder = 8;
    this.halo.renderOrder = 8;
    this.burstCore.renderOrder = 8;
    this.group.add(this.core, this.halo, this.burstCore);

    const budget = SHARDS[tier] ?? SHARDS.balanced;
    for (let i = 0; i < budget; i++) {
      const mesh = new THREE.Mesh(
        SHARD,
        new THREE.MeshStandardMaterial({
          color: 0x2a1620, roughness: 0.12, metalness: 0.5,
          envMapIntensity: 1.4, flatShading: true, transparent: true,
        }),
      );
      mesh.visible = false;
      this.debris.add(mesh);
      this.shards.push({ mesh, vel: new THREE.Vector3(), spin: new THREE.Vector3(), life: 0, size: 1 });
    }
  }

  /** The floor's one colour, so the muzzle halo belongs to the map it is on. */
  setAccent(hex: number): void {
    this.accent.setHex(hex);
    (this.halo.material as THREE.MeshBasicMaterial).color.copy(this.accent);
  }

  /**
   * A light for something that is arriving, or nothing if they are all out.
   *
   * The caller drives it while the arrival lasts and hands it back; a light
   * that is never returned is a light that stays on in an empty room.
   */
  claimSpawnLight(): THREE.PointLight | null {
    return this.spare.pop() ?? null;
  }

  releaseSpawnLight(l: THREE.PointLight): void {
    l.intensity = 0;
    if (!this.spare.includes(l)) this.spare.push(l);
  }

  /** A round in flight, drawn as the line it took. */
  tracer(a: THREE.Vector3, b: THREE.Vector3, colour = 0xffffff): void {
    const geo = new THREE.BufferGeometry().setFromPoints([a, b]);
    // Depth-tested, so a round is hidden by the wall it went behind and by the
    // weapon that fired it. Without this the tracer is transparent, transparent
    // draws after every opaque object however high its renderOrder, and the
    // shot appears to pass through the gun in your own hands.
    const line = new THREE.Line(
      geo,
      // Unlit and over the threshold: a tracer is a line of light, and a line
      // of light that does not bloom is a scratch on the lens.
      new THREE.LineBasicMaterial({ color: colour, transparent: true, opacity: 1, toneMapped: false }),
    );
    line.renderOrder = 7;
    line.userData.life = TRACER_LIFE;
    this.tracers.add(line);
  }

  /**
   * Light the room for an instant from where the shot left the barrel, and
   * put a hard white core there for forty milliseconds.
   *
   * No global exposure pulse. It is the right instinct — a real camera does
   * lift when a flash goes off in front of it — but at fifteen rounds a second
   * a whole-frame exposure change is a strobe, and the tier that can least
   * afford a full-screen pass is the one holding the machine pistol.
   */
  flash(at: THREE.Vector3, along: THREE.Vector3, strength = 7): void {
    this.muzzle.position.copy(at).addScaledVector(along, 0.4);
    this.muzzle.intensity = strength;
    this.core.position.copy(at).addScaledVector(along, 0.5);
    this.halo.position.copy(this.core.position);
    // Face the halo down the barrel, so it is a ring seen head-on from behind
    // the gun and an edge from the side, which is what a flash does.
    this.halo.lookAt(this.core.position.clone().addScaledVector(along, 1));
    this.coreLife = CORE_LIFE;
    this.core.visible = true;
    this.halo.visible = true;
  }

  /**
   * Chips off something that has just been hit.
   *
   * Thrown back along the shot with a wide spread, because the read is "that
   * came off the thing I hit" and a tight cone reads as a spark instead.
   */
  chips(at: THREE.Vector3, from: THREE.Vector3, count = 5, hot = false): void {
    const back = new THREE.Vector3().subVectors(at, from).normalize().multiplyScalar(-1);
    for (let i = 0; i < count; i++) {
      const s = this.take();
      s.size = 0.05 + (i % 3) * 0.018;
      s.life = SHARD_LIFE * 0.45;
      s.mesh.position.copy(at);
      s.mesh.scale.setScalar(s.size);
      s.mesh.visible = true;
      const m = s.mesh.material as THREE.MeshStandardMaterial;
      m.opacity = 1;
      m.emissive.setHex(hot ? 0xff3158 : 0x000000);
      m.emissiveIntensity = hot ? 2.2 : 0;
      s.vel.set(
        back.x * 2.4 + (Math.random() - 0.5) * 3.4,
        1.4 + Math.random() * 2.2,
        back.z * 2.4 + (Math.random() - 0.5) * 3.4,
      );
      s.spin.set((Math.random() - 0.5) * 22, (Math.random() - 0.5) * 22, (Math.random() - 0.5) * 22);
    }
  }

  /**
   * Break a body into its pieces and let them fall.
   *
   * The parts are cloned from whatever was standing there, so a hulk comes
   * apart into hulk-sized pieces and a drone into drone-sized ones without
   * this knowing anything about either. They are thrown away from the blow
   * that killed them, tumble, and stop when they reach the ground the caller
   * says is underneath.
   *
   * Capped by reusing the oldest: a long fight leaves a floor with a fixed
   * number of bodies on it rather than an unbounded one, and the ones that
   * disappear are the ones you killed longest ago and are least likely to be
   * looking at.
   */
  fell(pieces: THREE.Object3D[], from: THREE.Vector3, force = 1): void {
    for (const src of pieces) {
      const mesh = src as THREE.Mesh;
      if (!mesh.isMesh || !mesh.visible) continue;
      const world = new THREE.Vector3();
      mesh.getWorldPosition(world);
      // Nothing at the origin: a part that was never positioned would rain
      // from the middle of the map.
      if (!Number.isFinite(world.x)) continue;

      if (this.parts.length >= MAX_PARTS) {
        const old = this.parts.shift()!;
        this.remains.remove(old.mesh);
        old.mesh.geometry.dispose?.();
      }
      const copy = new THREE.Mesh(mesh.geometry.clone(), mesh.material);
      copy.position.copy(world);
      mesh.getWorldQuaternion(copy.quaternion);
      mesh.getWorldScale(copy.scale);
      copy.castShadow = false;
      this.remains.add(copy);

      const away = new THREE.Vector3().subVectors(world, from);
      if (away.lengthSq() < 1e-6) away.set(0, 1, 0);
      away.normalize();
      const size = Math.max(0.12, copy.scale.length() * 0.4);
      this.parts.push({
        mesh: copy,
        vel: new THREE.Vector3(
          away.x * (1.4 + Math.random() * 2.2) * force,
          1.6 + Math.random() * 2.6,
          away.z * (1.4 + Math.random() * 2.2) * force,
        ),
        spin: new THREE.Vector3(
          (Math.random() - 0.5) * 9, (Math.random() - 0.5) * 9, (Math.random() - 0.5) * 9,
        ),
        radius: size,
        resting: false,
        bornY: world.y,
      });
    }
  }

  /**
   * Shove anything lying near a point, so walking through a body moves it.
   *
   * Called with the player's feet every frame. It is not a physical response —
   * nothing pushes back and the player does not slow down — because a corpse
   * that can block a doorway is a corpse that can end a run, and the thing
   * being bought here is the feeling that the floor has stuff on it.
   */
  disturb(at: THREE.Vector3, radius = 0.8): void {
    for (const p of this.parts) {
      if (!p.mesh.visible) continue;
      let dx = p.mesh.position.x - at.x;
      let dz = p.mesh.position.z - at.z;
      if (Math.abs(p.mesh.position.y - at.y) > 1.6) continue;
      let d2 = dx * dx + dz * dz;
      const r = radius + p.radius;
      if (d2 > r * r) continue;
      /*
       * Standing exactly on one is the case that matters most.
       *
       * There is no direction to push in when the distance is zero, and the
       * first version simply skipped it — so the one piece you were actually
       * stood on was the only one that never moved, which is precisely what
       * somebody notices. Any direction will do; it only has to be one.
       */
      if (d2 < 1e-4) {
        const a = Math.random() * Math.PI * 2;
        dx = Math.cos(a);
        dz = Math.sin(a);
        d2 = 1;
      }
      const d = Math.sqrt(d2);
      const push = (1 - Math.min(1, d / r)) * 3.4 + 0.6;
      p.vel.x += (dx / d) * push;
      p.vel.z += (dz / d) * push;
      p.vel.y = Math.max(p.vel.y, 0.7);
      p.spin.y += (Math.random() - 0.5) * 5;
      p.resting = false;
    }
  }

  /** Where the ground is under a piece; supplied by the caller each frame. */
  groundAt: ((x: number, z: number, y: number) => number) | null = null;

  /**
   * Pay for the bullet holes before anybody fires one.
   *
   * `renderer.compile` walks the scene and builds programs, which is why the
   * warm pass makes every hidden mesh visible first — but it does not upload
   * textures. The decal atlas is a canvas, so the first round that hit a wall
   * paid for the upload and the program together: one frame of 231ms, right
   * in the middle of the first fight, which is precisely where a hitch is
   * least forgivable.
   */
  warm(renderer: THREE.WebGLRenderer): void {
    if (this.holeTex) renderer.initTexture(this.holeTex);
  }

  /**
   * Leave a mark where a round went into a surface.
   *
   * Oldest one is reused rather than the pool being grown, so a long fight
   * costs a fixed amount and the marks behind you quietly recycle. They do not
   * fade on a timer: a hole in a wall does not heal, and a fixed ring of them
   * is its own limit.
   */
  hole(at: THREE.Vector3, normal: THREE.Vector3, size = 0.09): void {
    if (!this.holes.length) return;
    const m = this.holes[this.nextHole];
    this.nextHole = (this.nextHole + 1) % this.holes.length;
    // A millimetre off the surface. Coplanar with it, the depth test decides
    // which one wins per pixel and the mark crawls with the camera.
    m.position.copy(at).addScaledVector(normal, 0.002);
    m.lookAt(m.position.clone().add(normal));
    // Turned in its own plane, so forty-eight identical discs do not read as
    // one repeated stamp.
    m.rotateZ(Math.random() * Math.PI * 2);
    m.scale.setScalar(size * (0.82 + Math.random() * 0.4));
    m.visible = true;
  }

  /**
   * Something coming apart.
   *
   * The shell fractures into faceted pieces and the core it was containing
   * expands for a moment and goes out. That is the whole event: no fireball,
   * no shockwave ring, nothing that needs a soft texture — the reason it reads
   * is that the pieces are the same crystal the thing was made of a frame ago.
   */
  burst(at: THREE.Vector3, colour = 0xff3158, count = 16): void {
    for (let i = 0; i < count; i++) {
      const s = this.take();
      s.size = 0.07 + (i % 5) * 0.03;
      s.life = SHARD_LIFE * (0.7 + (i % 4) * 0.1);
      s.mesh.position.copy(at);
      s.mesh.scale.setScalar(s.size);
      s.mesh.visible = true;
      const m = s.mesh.material as THREE.MeshStandardMaterial;
      m.opacity = 1;
      // A few pieces carry the core out with them, which is what makes a
      // fracture read as something breaking open rather than breaking up.
      const lit = i % 4 === 0;
      m.emissive.setHex(lit ? colour : 0x000000);
      m.emissiveIntensity = lit ? 2.6 : 0;
      const a = (i / count) * Math.PI * 2;
      const up = 0.4 + Math.random() * 1.6;
      const out = 2.2 + Math.random() * 3.4;
      s.vel.set(Math.cos(a) * out, up * 2.6, Math.sin(a) * out);
      s.spin.set((Math.random() - 0.5) * 26, (Math.random() - 0.5) * 26, (Math.random() - 0.5) * 26);
    }
    // The core, going out: it opens wide and is gone in a sixth of a second,
    // which is the whole difference between something breaking open and
    // something breaking up.
    this.burstCore.position.copy(at);
    (this.burstCore.material as THREE.MeshBasicMaterial).color.setHex(colour);
    this.burstCore.visible = true;
    this.burstLife = 0.17;
  }

  /** The oldest shard, whether or not it had finished. */
  private take(): Shard {
    const s = this.shards[this.nextShard];
    this.nextShard = (this.nextShard + 1) % this.shards.length;
    return s;
  }

  update(dt: number): void {
    this.muzzle.intensity = Math.max(0, this.muzzle.intensity - dt * 70);

    if (this.coreLife > 0) {
      this.coreLife -= dt;
      const t = Math.max(0, this.coreLife / CORE_LIFE);
      // Shrinking rather than growing: a flash is brightest at the instant it
      // happens and everything after it is the eye catching up.
      this.core.scale.setScalar(0.05 + t * 0.13);
      this.halo.scale.setScalar(1.1 - t * 0.35);
      (this.core.material as THREE.MeshBasicMaterial).opacity = t;
      (this.halo.material as THREE.MeshBasicMaterial).opacity = t * 0.7;
      if (this.coreLife <= 0) {
        this.core.visible = false;
        this.halo.visible = false;
      }
    }

    if (this.burstLife > 0) {
      this.burstLife -= dt;
      const t = Math.max(0, this.burstLife / 0.17);
      this.burstCore.scale.setScalar(0.25 + (1 - t) * 1.5);
      (this.burstCore.material as THREE.MeshBasicMaterial).opacity = t * t;
      if (this.burstLife <= 0) this.burstCore.visible = false;
    }

    for (const s of this.shards) {
      if (s.life <= 0) continue;
      s.life -= dt;
      if (s.life <= 0) {
        s.mesh.visible = false;
        continue;
      }
      s.vel.y -= 15 * dt;
      s.mesh.position.addScaledVector(s.vel, dt);
      s.mesh.rotation.x += s.spin.x * dt;
      s.mesh.rotation.y += s.spin.y * dt;
      s.mesh.rotation.z += s.spin.z * dt;
      const m = s.mesh.material as THREE.MeshStandardMaterial;
      /*
       * Solid until the end, then gone.
       *
       * A shard that starts fading the moment it appears is a ghost for its
       * whole life; a shard that is opaque until the last fifth is a piece of
       * something that then stops being there. Only the tail is transparent.
       */
      const t = s.life / SHARD_LIFE;
      m.opacity = Math.min(1, t * 5);
      if (m.emissiveIntensity > 0) m.emissiveIntensity = Math.max(0, m.emissiveIntensity - dt * 9);
    }

    /*
     * The pieces, falling and then lying there.
     *
     * Deliberately not a rigid-body solver: a box, a floor height and a bounce
     * that loses most of its energy is the whole model. What it has to get
     * right is that things end up on the ground at rest and stay put, because
     * anything that jitters on the floor for ever is worse than a body that
     * faded out.
     */
    for (const p of this.parts) {
      if (p.resting) continue;
      p.vel.y -= 19 * dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.mesh.rotation.x += p.spin.x * dt;
      p.mesh.rotation.y += p.spin.y * dt;
      p.mesh.rotation.z += p.spin.z * dt;
      const floor = this.groundAt
        ? this.groundAt(p.mesh.position.x, p.mesh.position.z, p.mesh.position.y)
        : 0;
      const rest = floor + p.radius * 0.5;
      /*
       * A piece that has left the world stops being a piece.
       *
       * Thrown outward from a body killed near an edge, some of them land on
       * nothing at all — and with nothing under them the ground query has no
       * answer, so they fell for ever. Four of the first six did, at eight
       * hundred metres a second by the time anyone looked. Eight metres below
       * where it came apart is far enough to be certain and near enough that
       * nobody watches it happen.
       */
      if (p.mesh.position.y < p.bornY - 8) {
        p.mesh.visible = false;
        p.resting = true;
        continue;
      }
      if (p.mesh.position.y <= rest) {
        p.mesh.position.y = rest;
        // Most of the energy goes into the floor. Two or three small bounces
        // and it is down.
        if (p.vel.y < -1.2) {
          p.vel.y = -p.vel.y * 0.22;
          p.vel.x *= 0.55;
          p.vel.z *= 0.55;
          p.spin.multiplyScalar(0.5);
        } else {
          p.vel.set(0, 0, 0);
          p.spin.set(0, 0, 0);
          // Lie down rather than stand on a corner.
          p.mesh.rotation.x = Math.round(p.mesh.rotation.x / (Math.PI / 2)) * (Math.PI / 2);
          p.mesh.rotation.z = Math.round(p.mesh.rotation.z / (Math.PI / 2)) * (Math.PI / 2);
          p.resting = true;
        }
      } else {
        p.vel.x *= 1 - 0.6 * dt;
        p.vel.z *= 1 - 0.6 * dt;
      }
    }

    for (let i = this.tracers.children.length - 1; i >= 0; i--) {
      const l = this.tracers.children[i] as THREE.Line;
      l.userData.life -= dt;
      const m = l.material as THREE.LineBasicMaterial;
      m.opacity = Math.max(0, l.userData.life / TRACER_LIFE);
      if (l.userData.life <= 0) {
        l.geometry.dispose();
        m.dispose();
        this.tracers.remove(l);
      }
    }
  }

  /** Between floors: nothing in flight belongs to the next one. */
  clear(): void {
    for (const child of [...this.tracers.children]) {
      const l = child as THREE.Line;
      l.geometry?.dispose?.();
      (l.material as THREE.Material)?.dispose?.();
    }
    this.tracers.clear();
    for (const s of this.shards) {
      s.life = 0;
      s.mesh.visible = false;
    }
    // The marks and the bodies belong to the floor they were made on.
    for (const h of this.holes) h.visible = false;
    this.nextHole = 0;
    for (const p of this.parts) {
      this.remains.remove(p.mesh);
      p.mesh.geometry.dispose?.();
    }
    this.parts.length = 0;
    this.muzzle.intensity = 0;
    this.coreLife = 0;
    this.burstLife = 0;
    this.core.visible = false;
    this.halo.visible = false;
    this.burstCore.visible = false;
  }
}
